/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from "react";
import { listEvaluations } from "../src/services/evaluationsApi.js";
import { Card, CardContent, CardHeader, CardTitle } from "../src/components/ui/card.jsx";
import { Button } from "../src/components/ui/button.jsx";
import { Badge } from "../src/components/ui/badge.jsx";
import { Input } from "../src/components/ui/input.jsx";
import { Plus, Eye, Copy, Check } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { createPageUrl, formatDate } from "../src/utils/index.js";

export default function TestCases() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [stateFilter, setStateFilter] = useState(searchParams.get('state') || "");
  const [authFilter, setAuthFilter] = useState(searchParams.get('auth') || "all"); // 'all' | 'anonymous' | 'signed_in'
  const [openConfigIds, setOpenConfigIds] = useState({});
  // status filter removed
  const [presetFilter, setPresetFilter] = useState(searchParams.get('preset') || "all"); // 'all' | preset_type values
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('pageSize')) || 10);
  // Collections: tag filter + saved collections (client-side)
  const [collectionFilter, setCollectionFilter] = useState(searchParams.get('collection') || "all");
  const [savedCollections, setSavedCollections] = useState(() => {
    try { return JSON.parse(localStorage.getItem('saved-testcase-collections') || '[]'); } catch { return []; }
  });
  const [selectedSavedId, setSelectedSavedId] = useState("");
  const [newCollectionName, setNewCollectionName] = useState("");
  // --- Selection-based custom collections (explicit set of test case IDs) ---
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedFilterActive, setSelectedFilterActive] = useState(false);
  const [remoteCollections, setRemoteCollections] = useState([]); // from backend
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [orgIdForCollections, setOrgIdForCollections] = useState("");
  const [activeView, setActiveView] = useState(null); // { id, name, kind: 'selection' | 'criteria' }
  
  // Debug activeView changes
  useEffect(() => {
    console.log('activeView changed:', activeView);
  }, [activeView]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  useEffect(() => {
    loadTestCases();
  }, []);

  const loadTestCases = async () => {
    try {
      const cases = await listEvaluations({ limit: 200 });
      const normalized = Array.isArray(cases) ? cases.map((tc) => ({
        ...tc,
        collections: Array.isArray(tc?.collections)
          ? tc.collections
          : (Array.isArray(tc?.meta?.collections) ? tc.meta.collections : []),
      })) : [];
      setTestCases(normalized);
      // derive dominant org_id among loaded cases for org-scoped collections
      const counts = {};
      for (const tc of normalized) {
        const oid = tc?.config_data?.org_id;
        if (oid) counts[oid] = (counts[oid] || 0) + 1;
      }
      const dominant = Object.entries(counts).sort((a,b) => b[1]-a[1])[0]?.[0] || "";
      setOrgIdForCollections(dominant);
      if (dominant) {
        fetchRemoteCollections(dominant);
      }
    } catch (error) {
      console.error("Error loading test cases:", error);
    }
    setLoading(false);
  };
  const fetchRemoteCollections = async (orgId) => {
    try {
      setRemoteLoading(true);
      const qs = new URLSearchParams({ scope: 'org', org_id: orgId });
      const res = await fetch(`/api/collections?${qs.toString()}`);
      if (!res.ok) throw new Error(`List failed: ${res.status}`);
      const json = await res.json();
      const items = Array.isArray(json?.items) ? json.items : [];
      setRemoteCollections(items);
    } catch (e) {
      console.error('Failed to load shared collections', e);
    } finally {
      setRemoteLoading(false);
    }
  };

  const saveCurrentAsRemoteCollection = async () => {
    const name = newCollectionName.trim();
    if (!name) return;
    if (!orgIdForCollections) {
      alert('No org_id detected from test cases. Add org_id to your cases first.');
      return;
    }
    
    // Check if we have meaningful criteria to save
    const hasSelectionCriteria = selectedFilterActive && selectedIds.size > 0;
    const hasFilterCriteria = stateFilter || authFilter !== 'all' || presetFilter !== 'all' || searchQuery.trim();
    
    if (!hasSelectionCriteria && !hasFilterCriteria) {
      alert('No filters or selection active. Please apply some filters or select test cases before saving a collection.');
      return;
    }
    
    try {
      // Debug: Log current filter state
      console.log('Current filter state when saving collection:', {
        selectedFilterActive,
        selectedIds: Array.from(selectedIds),
        stateFilter,
        authFilter,
        presetFilter,
        searchQuery
      });

      // If user has an explicit selection filter active and at least one id, save as selection collection.
      // Otherwise, save current filter criteria as a criteria-based collection.
      const criteria = hasSelectionCriteria
        ? { ids: Array.from(selectedIds) }
        : { 
            stateFilter: stateFilter || "", 
            authFilter: authFilter || "all", 
            presetFilter: presetFilter || "all", 
            searchQuery: searchQuery || "" 
          };

      console.log('Saving collection with criteria:', criteria);

      const payload = {
        name,
        criteria,
        scope: 'org',
        org_id: orgIdForCollections,
      };
      const res = await fetch('/api/collections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Create failed: ${res.status}`);
      const created = await res.json();
      const etag = res.headers.get('ETag') || '';
      const withEtag = etag ? { ...created, _etag: etag } : created;
      setRemoteCollections((prev) => {
        const withoutSame = prev.filter((c) => c.id !== withEtag.id);
        return [...withoutSame, withEtag];
      });
      setNewCollectionName('');

      // Reflect the applied/created collection in the UI
      const kind = Array.isArray(criteria.ids) ? 'selection' : 'criteria';
      setActiveView({ id: withEtag.id, name, kind });
      if (kind === 'selection') {
        setSelectedIds(new Set(criteria.ids));
        setSelectedFilterActive(true);
      }
    } catch (e) {
      console.error('Failed to save collection (shared)', e);
      alert(`Save failed: ${e.message || e}`);
    }
  };

  const applyAnySavedCollection = (idOrName) => {
    // Clear the selected saved ID since we're applying a collection
    setSelectedSavedId("");
    
    console.log('Applying collection:', idOrName);
    
    // try remote first (by id or name)
    const remote = remoteCollections.find((c) => c.id === idOrName) || remoteCollections.find((c) => c.name === idOrName);
    if (remote) {
      console.log('Found remote collection:', remote);
      const ids = Array.isArray(remote?.criteria?.ids) ? new Set(remote.criteria.ids) : null;
      if (ids && ids.size) {
        console.log('Applying selection collection with IDs:', Array.from(ids));
        setSelectedIds(ids);
        setSelectedFilterActive(true);
        setActiveView({ id: remote.id, name: remote.name, kind: 'selection' });
        return;
      }
      // fallback: apply traditional criteria if present
      const crit = remote.criteria || {};
      console.log('Applying criteria collection with criteria:', crit);
      setStateFilter(crit.stateFilter || "");
      setAuthFilter(crit.authFilter || "all");
      setPresetFilter(crit.presetFilter || "all");
      setSearchQuery(crit.searchQuery || "");
      setSelectedFilterActive(false);
      setActiveView({ id: remote.id, name: remote.name, kind: 'criteria' });
      return;
    }
    // then try local (legacy) saved collections
    const local = savedCollections.find((c) => c.id === idOrName) || savedCollections.find((c) => c.name === idOrName);
    if (local) {
      console.log('Found local collection:', local);
      const { stateFilter: s = "", authFilter: a = "all", presetFilter: p = "all", searchQuery: q = "" } = local.criteria || {};
      setStateFilter(s);
      setAuthFilter(a);
      setPresetFilter(p);
      setSearchQuery(q);
      setSelectedFilterActive(false);
      setActiveView({ id: local.id, name: local.name, kind: 'criteria' });
    }
  };

  const deleteRemoteCollection = async (id) => {
    const item = remoteCollections.find((c) => c.id === id);
    if (!item) return;
    try {
      const headers = {};
      if (item._etag) headers['If-Match'] = item._etag;
      const res = await fetch(`/api/collections/${encodeURIComponent(id)}?org_id=${encodeURIComponent(orgIdForCollections)}`, {
        method: 'DELETE',
        headers,
      });
      if (res.status !== 204) throw new Error(`Delete failed: ${res.status}`);
      setRemoteCollections((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      console.error('Failed to delete shared collection', e);
      alert(`Delete failed: ${e.message || e}`);
    }
  };
  // Persist saved collections to localStorage
  useEffect(() => {
    try { localStorage.setItem('saved-testcase-collections', JSON.stringify(savedCollections)); } catch {}
  }, [savedCollections]);

  const handleCopyConfig = (configData, id) => {
    navigator.clipboard.writeText(JSON.stringify(configData, null, 2));
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  // status color removed

  const getPresetColor = (preset) => {
    switch (preset) {
      case 'anonymous': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200';
      case 'address_only': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200';
      case 'logged_in': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
      default: return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200';
    }
  };

  const deriveAuthStatus = (tc) => {
    const explicit = tc.scenario_auth || tc.auth_status;
    if (explicit === 'signed_in' || explicit === 'anonymous') return explicit;
    const cfg = tc.config_data || {};
    const looksLoggedIn = Boolean((cfg.user_id && String(cfg.user_id).trim()) || (cfg.email && String(cfg.email).trim()));
    return looksLoggedIn ? 'signed_in' : 'anonymous';
  };

  // Gather all collection tags from test cases
  const allCollectionTags = Array.from(new Set(testCases.flatMap(tc => Array.isArray(tc.collections) ? tc.collections : [])));


  const applySavedCollection = (idOrName) => {
    const c = savedCollections.find((c) => c.id === idOrName) || savedCollections.find((c) => c.name === idOrName);
    if (!c) return;
    const { stateFilter: s = "", authFilter: a = "all", presetFilter: p = "all", searchQuery: q = "" } = c.criteria || {};
    setStateFilter(s);
    setAuthFilter(a);
    setPresetFilter(p);
    setSearchQuery(q);
  };

  const deleteSavedCollection = (id) => {
    setSavedCollections((prev) => prev.filter((c) => c.id !== id));
    if (selectedSavedId === id) setSelectedSavedId("");
  };

  const filteredTestCases = testCases.filter((tc) => {
    // If we have an active collection view, apply its filtering logic
    if (activeView) {
      if (activeView.kind === 'selection') {
        // For selection-based collections, filter by the selected IDs
        const matches = selectedIds.has(tc.id);
        if (tc.id === testCases[0]?.id) { // Debug first test case
          console.log(`Selection filter for ${tc.id}: ${matches} (selectedIds: ${Array.from(selectedIds)})`);
        }
        return matches;
      } else if (activeView.kind === 'criteria') {
        // For criteria-based collections, apply the collection's criteria
        // This will be handled by the individual filter states that were set when applying the collection
        if (tc.id === testCases[0]?.id) { // Debug first test case
          console.log(`Criteria filter active for ${tc.id}, filters:`, { stateFilter, authFilter, presetFilter, searchQuery });
        }
      }
    }
    
    // If we have an active selection filter (independent of collections), use that
    if (selectedFilterActive && selectedIds.size > 0) {
      const matches = selectedIds.has(tc.id);
      if (tc.id === testCases[0]?.id) { // Debug first test case
        console.log(`Independent selection filter for ${tc.id}: ${matches}`);
      }
      return matches;
    }
    
    const matchesState = stateFilter ? (tc?.config_data?.state === stateFilter) : true;
    const auth = deriveAuthStatus(tc);
    const matchesAuth = authFilter === 'all' ? true : (auth === authFilter);
    const matchesStatus = true; // status removed
    const matchesPreset = presetFilter === 'all' ? true : (tc.preset_type === presetFilter);
    const matchesCollectionTag = collectionFilter === 'all'
      ? true
      : (Array.isArray(tc.collections) && tc.collections.includes(collectionFilter));
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = q
      ? [
          tc.title,
          tc.user_input,
          tc?.config_data?.project?.council_name,
          tc?.config_data?.project?.address,
          tc?.config_data?.project_id,
        ]
          .map((v) => (typeof v === 'string' ? v.toLowerCase() : ''))
          .some((s) => s.includes(q))
      : true;
    
    if (tc.id === testCases[0]?.id) { // Debug first test case
      console.log(`Standard filter for ${tc.id}:`, { matchesState, matchesAuth, matchesPreset, matchesCollectionTag, matchesSearch });
    }
    
    return matchesState && matchesAuth && matchesStatus && matchesPreset && matchesSearch && matchesCollectionTag;
  });

  const toggleConfigOpen = (id) => {
    setOpenConfigIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Reset to page 1 on any filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [stateFilter, authFilter, presetFilter, collectionFilter, searchQuery, testCases, activeView]);

  // Sync filters/search/pagination to URL query params
  useEffect(() => {
    const params = {};
    const q = searchQuery.trim();
    if (q) params.q = q;
    if (stateFilter) params.state = stateFilter;
    if (authFilter !== 'all') params.auth = authFilter;
    // status removed
    if (presetFilter !== 'all') params.preset = presetFilter;
    if (collectionFilter !== 'all') params.collection = collectionFilter;
    if ((Number.isFinite(currentPage) && currentPage > 1)) params.page = String(currentPage);
    if (pageSize !== 10) params.pageSize = String(pageSize);
    setSearchParams(params);
  }, [stateFilter, authFilter, presetFilter, collectionFilter, searchQuery, currentPage, pageSize, setSearchParams]);

  const totalCount = filteredTestCases.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);
  const pagedTestCases = filteredTestCases.slice(startIndex, endIndex);
  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  const getValueByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  };

  const computeCompletionPct = (tc) => {
    const config = tc?.config_data || {};
    const required = [
      'org_id',
      'state',
      'project.council_name',
      'project.address',
      'project.active_precinct',
      'project_id',
      'conversation_id',
      'user_id',
    ];
    let filled = required.reduce((count, path) => {
      const val = getValueByPath(config, path);
      const ok = typeof val === 'string' ? (val.trim() ? 1 : 0) : (val ? 1 : 0);
      return count + ok;
    }, 0);
    const titleFilled = typeof tc?.title === 'string' && tc.title.trim() !== '';
    const chatInputFilled = typeof tc?.user_input === 'string' && tc.user_input.trim() !== '';
    const expectedAnswerPresent = typeof tc?.expected_response_answer === 'string' && tc.expected_response_answer.trim() !== '';
    const expectedQuestionPresent = typeof tc?.expected_response_question === 'string' && tc.expected_response_question.trim() !== '';
    const hasOneExpected = expectedAnswerPresent || expectedQuestionPresent;
    const extraTotal = 3;
    const extraFilled = (titleFilled ? 1 : 0) + (chatInputFilled ? 1 : 0) + (hasOneExpected ? 1 : 0);
    const total = required.length + extraTotal;
    filled += extraFilled;
    return total > 0 ? Math.round((filled / total) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid gap-6">
            {Array(3).fill(0).map((_, i) => (
              <Card key={i} className="animate-pulse dark:bg-slate-900/60">
                <CardContent className="p-6">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-4"></div>
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">Test Cases</h1>
            <p className="text-slate-600 dark:text-slate-400">Manage your AI evaluation test cases</p>
          </div>
          <Link to={createPageUrl("CreateTest")}>
            <Button className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2" data-tour="testcases-create-new">
              <Plus className="w-4 h-4" />
              Create Test Case
            </Button>
          </Link>
        </div>

        {testCases.length === 0 ? (
          <Card className="shadow-lg border-0 bg-white/80 dark:bg-slate-900/60 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">No test cases yet</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">Get started by creating your first AI evaluation test case</p>
              <Link to={createPageUrl("CreateTest")}>
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Test Case
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            <Card className="shadow-sm border dark:border-slate-700 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
              <CardContent className="p-3 md:p-4">
                <div className="grid gap-2 md:gap-3 md:grid-cols-12">
                  <div className="flex-1 md:col-span-8">
                    <Input
                      placeholder="Search title, input, council, address, project id..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full"
                      data-tour="testcases-search"
                    />
                  </div>
                  <div className="flex items-center gap-2 md:col-span-4 md:justify-end">
                    <span className="text-xs text-slate-600 dark:text-slate-400">Collection:</span>
                    <select
                      className="text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                      value={collectionFilter}
                      onChange={(e) => setCollectionFilter(e.target.value)}
                      title="Filter test cases by assigned collection tag"
                      data-tour="testcases-collection-filter"
                    >
                      <option value="all">All</option>
                      {allCollectionTags.map((tag) => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:col-span-12" data-tour="testcases-state-filters">
                    {["qa", "planning_pathway", "document_checklist", "application"].map((stateKey) => (
                      <button
                        key={stateKey}
                        className={`px-3 py-1.5 text-xs rounded border ${stateFilter === stateKey ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                        onClick={() => setStateFilter(stateKey)}
                        title={`Filter by ${stateKey.replace("_", " ")}`}
                      >
                        {stateKey.replace("_", " ")}
                      </button>
                    ))}
                    <button
                      className={`px-3 py-1.5 text-xs rounded border ${stateFilter === "" ? "bg-slate-50 border-slate-300 text-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                      onClick={() => setStateFilter("")}
                      title="Show all states"
                    >
                      All states
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:col-span-12" data-tour="testcases-auth-filters">
                    <button
                      className={`px-3 py-1.5 text-xs rounded border ${authFilter === "anonymous" ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                      onClick={() => setAuthFilter("anonymous")}
                      title="Anonymous"
                    >
                      Anonymous
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs rounded border ${authFilter === "signed_in" ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                      onClick={() => setAuthFilter("signed_in")}
                      title="Logged in"
                    >
                      Logged in
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs rounded border ${authFilter === "all" ? "bg-slate-50 border-slate-300 text-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                      onClick={() => setAuthFilter("all")}
                      title="Show all auth statuses"
                    >
                      All auth
                    </button>
                  </div>
                  {/* status filter removed */}
                  <div className="flex flex-wrap items-center gap-2 md:col-span-12" data-tour="testcases-preset-filters">
                    {["anonymous","address_only","logged_in","custom"].map((pt) => (
                      <button
                        key={pt}
                        className={`px-3 py-1.5 text-xs rounded border ${presetFilter === pt ? "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                        onClick={() => setPresetFilter(pt)}
                        title={`Preset: ${pt.replace('_',' ')}`}
                      >
                        {pt.replace('_',' ')}
                      </button>
                    ))}
                    <button
                      className={`px-3 py-1.5 text-xs rounded border ${presetFilter === "all" ? "bg-slate-50 border-slate-300 text-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700" : "bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}
                      onClick={() => setPresetFilter("all")}
                      title="All presets"
                    >
                      All presets
                    </button>
                  </div>
                  {/* Selection toolbar */}
                  <div className="flex items-center gap-2 md:col-span-12" data-tour="testcases-selection-toolbar">
                    <span className="text-xs text-slate-600 dark:text-slate-400">Selected:</span>
                    <span className="text-xs font-medium text-slate-800 dark:text-slate-200">{selectedIds.size}</span>
                    <button
                      className={`px-3 py-1.5 text-xs rounded border ${selectedFilterActive ? 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800' : 'bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200'}`}
                      onClick={() => setSelectedFilterActive((v) => !v)}
                      title="Toggle filter to selection"
                    >
                      {selectedFilterActive ? 'Viewing selection' : 'Filter to selection'}
                    </button>
                    <button
                      className="px-3 py-1.5 text-xs rounded border bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
                      onClick={clearSelection}
                      title="Clear selected rows"
                    >
                      Clear selection
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 md:col-span-12">
                    <input
                      type="text"
                      value={newCollectionName}
                      onChange={(e) => setNewCollectionName(e.target.value)}
                      placeholder="Name this collection…"
                      className="text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                    />
                    <Button
                      className="px-3 py-1.5 text-xs"
                      variant="outline"
                      onClick={saveCurrentAsRemoteCollection}
                      title="Save current filters or selection as a shared collection"
                    >
                      Save collection (shared)
                    </Button>
                    <select
                      className="text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                      value={selectedSavedId}
                      onChange={(e) => setSelectedSavedId(e.target.value)}
                      title="Choose a saved collection"
                    >
                      <option value="">Saved collections…</option>
                      {/* Remote shared collections first */}
                      {remoteCollections.map((c) => (
                        <option key={`r_${c.id}`} value={c.id}>{c.name} {Array.isArray(c?.criteria?.ids) ? ' (shared: selection)' : ' (shared)'}</option>
                      ))}
                      {/* Local (legacy) saved collections */}
                      {savedCollections.map((c) => (
                        <option key={`l_${c.id}`} value={c.id}>{c.name} (local)</option>
                      ))}
                    </select>
                    <Button
                      className="px-3 py-1.5 text-xs"
                      variant="outline"
                      onClick={() => applyAnySavedCollection(selectedSavedId)}
                      disabled={!selectedSavedId}
                      title="Apply the selected collection"
                    >
                      Apply
                    </Button>
                    {selectedSavedId && (
                      <Button
                        className="px-3 py-1.5 text-xs"
                        variant="outline"
                        onClick={() => {
                          const isRemote = remoteCollections.some((c) => c.id === selectedSavedId);
                          if (isRemote) {
                            deleteRemoteCollection(selectedSavedId);
                          } else {
                            deleteSavedCollection(selectedSavedId);
                          }
                        }}
                        title="Delete selected collection"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="md:col-span-12">
                    <button
                      className="px-3 py-1.5 text-xs rounded border bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
                      onClick={() => { setSearchQuery(""); setStateFilter(""); setAuthFilter("all"); setPresetFilter("all"); }}
                      title="Clear all filters"
                    >
                      Clear filters
                    </button>
                  </div>
                  {activeView && (
                    <div className="flex items-center gap-2 md:col-span-12">
                      <span className="text-xs rounded px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200" title="Active saved collection">
                        Collection: {activeView.name}{activeView.kind === 'selection' ? ' (selection)' : ''}
                      </span>
                      <button
                        className="px-2 py-1 text-xs rounded border bg-white dark:bg-slate-900/60 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
                        onClick={() => { 
                          setActiveView(null); 
                          setSelectedFilterActive(false); 
                          setSelectedIds(new Set());
                          setSelectedSavedId("");
                        }}
                        title="Clear active collection"
                      >
                        Clear collection
                      </button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            {pagedTestCases.length === 0 && (
              <Card className="shadow border border-dashed border-slate-200 dark:border-slate-700">
                <CardContent className="p-6 text-center text-slate-600 dark:text-slate-300">
                  No results match your filters.
                </CardContent>
              </Card>
            )}
            {pagedTestCases.map((testCase) => (
              <Card key={testCase.id} className="shadow-lg border dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 backdrop-blur-sm hover:shadow-xl transition-all duration-200">
                <CardHeader className="border-b border-slate-100 dark:border-slate-800">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selectedIds.has(testCase.id)}
                        onChange={() => toggleSelected(testCase.id)}
                        title="Select this test case"
                      />
                      <div>
                        <CardTitle className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">{testCase.title}</CardTitle>
                        <div className="flex gap-2 flex-wrap">
                          {/* status removed */}
                          <Badge className={getPresetColor(testCase.preset_type)}>
                            {testCase.preset_type?.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline" className="text-slate-600 dark:text-slate-300">
                            State: {testCase.config_data?.state || 'N/A'}
                          </Badge>
                          <Badge variant="outline" className="text-slate-600 dark:text-slate-300">
                            {deriveAuthStatus(testCase) === 'signed_in' ? 'Logged in' : 'Anonymous'}
                          </Badge>
                          <Badge variant="outline" className="text-slate-600 dark:text-slate-300">
                            {formatDate(testCase.created_date)}
                          </Badge>
                          {Array.isArray(testCase.collections) && testCase.collections.map((tag) => (
                            <Badge key={tag} variant="outline" className="text-slate-600 dark:text-slate-300">
                              #{tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleConfigOpen(testCase.id)}
                        className="flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        {openConfigIds[testCase.id] ? 'Hide Config' : 'View Config'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyConfig(testCase.config_data, testCase.id)}
                        className="flex items-center gap-2"
                      >
                        {copied === testCase.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied === testCase.id ? 'Copied!' : 'Copy Config'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">User Input</h4>
                      <p className="text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/70 p-3 rounded-lg text-sm">
                        {testCase.user_input}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">Expected Response</h4>
                      <div className="space-y-2">
                        {testCase.expected_response_answer && (
                          <div>
                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Answer:</span>
                            <p className="text-slate-600 dark:text-slate-300 bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded text-sm">
                              {testCase.expected_response_answer}
                            </p>
                          </div>
                        )}
                        {testCase.expected_response_question && (
                          <div>
                            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Question:</span>
                            <p className="text-slate-600 dark:text-slate-300 bg-blue-50 dark:bg-blue-900/20 p-2 rounded text-sm">
                              {testCase.expected_response_question}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {testCase.config_data && (
                    <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">Configuration Summary</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-slate-500 dark:text-slate-400">Org ID:</span>
                          <p className="text-slate-800 dark:text-slate-200 truncate">{testCase.config_data.org_id || 'Not set'}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-500 dark:text-slate-400">Council:</span>
                          <p className="text-slate-800 dark:text-slate-200 truncate">{testCase.config_data.project?.council_name || 'Not set'}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-500 dark:text-slate-400">Address:</span>
                          <p className="text-slate-800 dark:text-slate-200 truncate">{testCase.config_data.project?.address || 'Not set'}</p>
                        </div>
                        <div>
                           <span className="font-medium text-slate-500 dark:text-slate-400">Project ID:</span>
                           <p className="text-slate-800 dark:text-slate-200 truncate">{testCase.config_data.project_id || 'Not set'}</p>
                        </div>
                      </div>
                      {openConfigIds[testCase.id] && (
                        <div className="mt-4">
                          <h5 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Full User Config</h5>
                          <pre className="text-xs bg-slate-900 text-slate-200 p-4 rounded-lg overflow-auto max-h-80">{JSON.stringify(testCase.config_data, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {remoteLoading && (
              <div className="text-xs text-slate-500 dark:text-slate-400">Loading shared collections…</div>
            )}
            {totalCount > 0 && (
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Showing {startIndex + 1}-{endIndex} of {totalCount}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Rows per page:</span>
                    <select
                      className="text-sm border rounded px-2 py-1 bg-white dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700"
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    >
                      {[5,10,20,50].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handlePrev} disabled={safeCurrentPage <= 1}>Prev</Button>
                    <span className="text-sm text-slate-700 dark:text-slate-300">Page {safeCurrentPage} of {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={handleNext} disabled={safeCurrentPage >= totalPages}>Next</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}